import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { organizationsAPI } from '@/services';
import { useToast } from '../components/Toast';

const OrganizationRegister = () => {
  const [formData, setFormData] = useState({
    // Organisation
    name: '',
    subdomain: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    // Admin
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    admin_password_confirm: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { error: showError, success } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.admin_password !== formData.admin_password_confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.admin_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (!formData.subdomain || formData.subdomain.length < 3) {
      setError('Le sous-domaine doit contenir au moins 3 caractères');
      return;
    }

    // Valider le format du subdomain (alphanumeric et tirets uniquement)
    if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      setError('Le sous-domaine ne peut contenir que des lettres minuscules, chiffres et tirets');
      return;
    }

    setLoading(true);

    try {
      const { admin_password_confirm, ...dataToSend } = formData;
      const response = await organizationsAPI.register(dataToSend);
      
      success('Organisation créée avec succès ! Vous pouvez maintenant vous connecter.');
      navigate('/login');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erreur lors de la création de l\'organisation';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-gray-50 via-white to-entrea-panel/35">
      <div className="w-full flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-2xl mb-4 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Inscription Organisation
              </h2>
              <p className="text-gray-600">
                Créez votre organisation et commencez à gérer vos membres
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations Organisation */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de l'Organisation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom de l'organisation <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Ex: Mon Association"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sous-domaine <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        name="subdomain"
                        value={formData.subdomain}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-l-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                        placeholder="mon-association"
                        required
                        pattern="[a-z0-9-]+"
                      />
                      <span className="px-4 py-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-xl text-gray-600">
                        .mobile.entrea.app
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Lettres minuscules, chiffres et tirets uniquement</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="contact@organisation.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="+257 XX XX XX XX"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Adresse complète"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Description de votre organisation"
                    />
                  </div>
                </div>
              </div>

              {/* Informations Administrateur */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compte Administrateur</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="admin_name"
                      value={formData.admin_name}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Votre nom complet"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="admin@organisation.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      name="admin_phone"
                      value={formData.admin_phone}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="+257 XX XX XX XX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mot de passe <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="admin_password"
                      value={formData.admin_password}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Minimum 6 caractères"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirmer le mot de passe <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="admin_password_confirm"
                      value={formData.admin_password_confirm}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent transition"
                      placeholder="Confirmez le mot de passe"
                      required
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-entrea-ink to-entrea-royal hover:from-entrea-navy hover:to-entrea-royal text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? 'Création en cours...' : "Créer l'organisation"}
              </button>

              <div className="text-center pt-4">
                <p className="text-sm text-gray-600">
                  Déjà une organisation ?{' '}
                  <Link to="/login" className="font-semibold text-entrea-royal hover:text-entrea-ink transition">
                    Se connecter
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationRegister;

